import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { addDays, formatLongDate, todayISO } from "@/lib/format";
import { AdminCard, AdminButton, EmptyState, adminInput } from "@/components/admin/ui";
import { ReservationRow } from "@/pages/admin/AdminToday";
import ReservationDialog from "@/pages/admin/ReservationDialog";

const CLOSURE_REASONS = [
  "Chiuso",
  "Chiuso per evento privato",
  "Chiuso per motivi tecnici",
  "Chiuso per ferie",
];

export default function AdminCalendar() {
  const navigate = useNavigate();
  const [start, setStart] = useState(todayISO());
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(todayISO());
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const loadCalendar = useCallback(async (from) => {
    try {
      const { data } = await api.get("/admin/calendar", { params: { start: from, days: 21 } });
      setDays(data);
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  const loadDay = useCallback(async (date) => {
    try {
      const { data } = await api.get("/admin/summary", { params: { date } });
      setDetail(data);
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => {
    loadCalendar(start);
  }, [start, loadCalendar]);

  useEffect(() => {
    loadDay(selected);
  }, [selected, loadDay]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/reservations/${id}`, patch);
      await loadDay(selected);
      await loadCalendar(start);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const dayFull = Boolean(detail?.special && detail.special.online_booking_enabled === false && !detail.closed);
  const dayClosed = Boolean(detail?.closed);
  const [closeMenu, setCloseMenu] = useState(false);

  const setOpen = async (open, reason) => {
    const attive = (detail?.reservations || []).filter((r) => r.status !== "cancelled").length;
    if (!open && attive > 0) {
      const testo =
        attive === 1
          ? "C'è 1 prenotazione in questa data. Chiudo comunque? Le prenotazioni restano nel pannello e non vengono cancellate."
          : `Ci sono ${attive} prenotazioni in questa data. Chiudo comunque? Le prenotazioni restano nel pannello e non vengono cancellate.`;
      if (!window.confirm(testo)) return;
    }
    try {
      setCloseMenu(false);
      await api.put("/admin/day-open", { date: selected, open, reason: reason || "Chiuso" });
      await loadDay(selected);
      await loadCalendar(start);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const toggleFull = async () => {
    try {
      await api.put("/admin/day-full", { date: selected, full: !dayFull });
      await loadDay(selected);
      await loadCalendar(start);
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-calendar">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Calendario</p>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Prossimi giorni</h1>
        </div>
        <div className="flex items-center gap-2">
          <AdminButton variant="outline" onClick={() => setStart(addDays(start, -21))}>
            Indietro
          </AdminButton>
          <input
            type="date"
            value={start}
            onChange={(e) => e.target.value && setStart(e.target.value)}
            className={`${adminInput} w-auto`}
            data-testid="calendar-start"
          />
          <AdminButton variant="outline" onClick={() => setStart(addDays(start, 21))}>
            Avanti
          </AdminButton>
        </div>
      </div>

      {error && (
        <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2" data-testid="calendar-grid">
        {days.map((d) => {
          const pct = d.capacity ? Math.min(100, (d.guests / d.capacity) * 100) : 0;
          return (
            <button
              key={d.date}
              type="button"
              onClick={() => navigate(`/admin/prenotazioni?date=${d.date}`)}
              data-testid={`calendar-day-${d.date}`}
              className={`relative border p-3 text-left transition-colors duration-300 ${
                selected === d.date ? "border-sage bg-sage/10" : "border-border bg-cream hover:border-sage/50"
              }`}
            >
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(d.date);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelected(d.date);
                  }
                }}
                title="Gestisci il giorno"
                className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center text-wood/40 transition-colors hover:bg-sage/15 hover:text-wood"
                data-testid={`calendar-manage-${d.date}`}
              >
                <Settings2 size={14} strokeWidth={1.6} />
              </span>
              <p className="text-[0.6rem] uppercase tracking-[0.16em] text-wood/50">
                {formatLongDate(d.date).split(" ").slice(0, 3).join(" ")}
              </p>
              {d.closed ? (
                <p className="mt-2 text-sm text-foreground/45">Chiuso</p>
              ) : (
                <>
                  <p className="mt-2 font-serif text-2xl text-wood tabular-nums">
                    {d.guests}
                    <span className="text-sm text-wood/45">/{d.capacity}</span>
                  </p>
                  <div className="mt-2 h-1 bg-beige">
                    <div className="h-1 bg-sage" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="mt-1.5 text-xs text-foreground/50">{d.reservations} pren.</p>
                </>
              )}
              {d.special && (
                <p className="mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-gold">
                  {d.special.type === "closed"
                    ? "chiusura"
                    : d.special.extraordinary
                      ? "straordinario"
                      : d.special.online_booking_enabled === false
                        ? "completo"
                        : "speciale"}
                </p>
              )}
            </button>
          );
        })}
      </div>

      <AdminCard
        title={formatLongDate(selected)}
        action={
          <div className="relative flex flex-wrap items-center gap-2">
            {dayClosed ? (
              <AdminButton variant="outline" onClick={() => setOpen(true)} data-testid="calendar-open-day">
                Apri il giorno
              </AdminButton>
            ) : (
              <>
                <AdminButton
                  variant="outline"
                  onClick={() => setCloseMenu((v) => !v)}
                  data-testid="calendar-close-day"
                >
                  Chiudi il giorno
                </AdminButton>
                <AdminButton variant="outline" onClick={toggleFull} data-testid="calendar-toggle-full">
                  {dayFull ? "Riapri prenotazioni online" : "Segna come completo"}
                </AdminButton>
              </>
            )}
            <AdminButton
              variant="wood"
              onClick={() => setDialog({ mode: "create", date: selected })}
              data-testid="calendar-new-reservation"
            >
              Nuova
            </AdminButton>
            {closeMenu && (
              <div
                className="absolute right-0 top-full z-20 mt-2 w-64 border border-wood/15 bg-cream shadow-lg"
                data-testid="calendar-close-menu"
              >
                <p className="px-4 pt-3 pb-2 text-[0.65rem] uppercase tracking-[0.14em] text-wood/50">
                  Motivo della chiusura
                </p>
                {CLOSURE_REASONS.map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setOpen(false, reason)}
                    className="block w-full px-4 py-2.5 text-left text-sm text-wood transition-colors hover:bg-sage/10"
                    data-testid={`calendar-close-reason-${reason.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            )}
          </div>
        }
        testId="calendar-detail"
      >
        {detail?.closed && (
          <EmptyState title="Chiuso" description={detail.special?.reason || "Nessun servizio previsto."} />
        )}
        {detail && !detail.closed && (
          <>
            {detail.special?.extraordinary && (
              <p
                className="mb-4 border border-sage/40 bg-sage/10 px-4 py-3 text-sm text-wood"
                data-testid="calendar-extra-banner"
              >
                Apertura straordinaria: gli orari sono stati copiati dal giorno di apertura più vicino.
                Sul sito il giorno compare fra gli avvisi come &laquo;aperto in via straordinaria&raquo;.
              </p>
            )}
            {dayFull && (
              <p
                className="mb-4 border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-wood"
                data-testid="calendar-full-banner"
              >
                Giorno segnato come completo: le prenotazioni online sono chiuse. Puoi continuare ad
                aggiungerne a mano dal pulsante &laquo;Nuova&raquo;.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {detail.services.map((svc) => (
                <div key={svc.id} className="border border-border px-4 py-3">
                  <p className="font-serif text-lg text-wood">{svc.name}</p>
                  <p className="mt-1 text-sm text-foreground/65">
                    {svc.booked}/{svc.capacity} coperti · {Math.max(0, svc.capacity - svc.booked)} disponibili
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-5">
              {detail.reservations.length === 0 ? (
                <EmptyState title="Nessuna prenotazione" description="Il giorno è ancora libero." />
              ) : (
                <ul className="space-y-3">
                  {detail.reservations.map((res) => (
                    <ReservationRow
                      key={res.id}
                      res={res}
                      onUpdate={update}
                      onEdit={(r) => setDialog({ mode: "edit", reservation: r })}
                    />
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </AdminCard>

      {dialog && (
        <ReservationDialog
          {...dialog}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            loadDay(selected);
            loadCalendar(start);
          }}
        />
      )}
    </div>
  );
}
